function buildNewOutputJson(outputJson, calculatorState, buildingsHeights) {
  /** All tuneable thresholds in one place. */
  const CFG = {
    // Metric projection
    EARTH_R: 6378137, // WGS-84 semi-major axis (m)

    // Vertex snapping / edge dedup (§1) — 1 cm in meters
    SNAP_M: 0.01,
    // Minimum edge length to keep (§1) — 10 cm
    MIN_EDGE_M: 0.1,

    // Facet validity filters
    MIN_FACET_AREA_M2: 0.2,
    MAX_FACET_SLOPE_DEG: 80,
    MIN_DZ_SMALL: 0.05,
    SMALL_AREA_M2: 8,

    // Dihedral coplanar threshold (§3.2) — degrees
    DIHEDRAL_COPLANAR_DEG: 5,

    // Crest / trough inside-point sampling (§3.1)
    SAMPLE_EPS_M: 0.35, // ε offset into facet (0.20–0.50 m)
    CREST_TOL_M: 0.03, // z dead-zone (0.02–0.05 m)

    // Ridge vs Hip edge-slope threshold (§3.3) — rise/run
    RIDGE_MAX_SLOPE: 0.05, // ≈ 3°

    // Aspect-opposition threshold for extra ridge confidence (§3.3)
    OPPOSING_DOT: -0.5,

    // Boundary classification (§4)
    EAVE_MAX_EDGE_SLOPE: 0.08, // "horizontalness" — up to ~8% grade
    EAVE_MAX_ALIGN: 0.3, // max |dot(edgeDir, downslope)|
    EAVE_Z_TOL_M: 0.25, // within 25 cm of facet min-Z (primary eave band)
    EAVE_Z_TOL_SECONDARY_M: 0.7, // secondary eave band for contour-following edges
    EAVE_MAX_SLOPE_SOFT: 0.2, // secondary eave edge-slope ceiling
    GABLE_MIN_ALIGN: 0.7, // min downslope alignment
    GABLE_MIN_EDGE_SLOPE: 0.15, // min edge slope for strong gable signal
    GABLE_STEEP_SLOPE: 0.3, // clearly climbing → gable regardless of alignment
    GABLE_LOW_HR_THRESH: 0.35, // height-ratio below which edge is "low on facet"
    GABLE_MIN_LEN_LOW: 1.0, // min length (m) for gable when edge is low on facet

    // Flat-facet transition (shared edge where one facet is nearly flat)
    FLAT_TRANSITION_SLOPE_DEG: 5, // facet slope below this is "flat"

    // Post-processing (§5)
    MIN_ISLAND_M: 0.3, // re-label edges shorter than this
    TINY_FACET_M2: 1.7, // "dormers" — soft metadata only

    // Dormer valley detection (§5.5)
    DORMER_AREA_RATIO: 0.25, // max ratio: smallFacetArea / largeFacetArea
    DORMER_MAIN_ALIGN: 0.35, // min alignment of edge with large facet's slope
    DORMER_MIN_LEN_M: 1.0, // min edge length for dormer valley

    // Short-edge noise filter for shared edges
    MIN_INTERNAL_EDGE_M: 0.5,

    // Z-gap threshold: if two segments share the same 2D edge but their
    // Z-values differ by more than this at either vertex, they are NOT
    // a true shared edge — they are separate boundary edges at different
    // heights (e.g. eave of upper facet vs. eave of lower facet).
    SHARED_Z_GAP_M: 0.5,

    // Collinear merge: max turn angle (degrees) between consecutive
    // boundary edges to consider them part of the same straight line.
    COLLINEAR_MAX_ANGLE_DEG: 5,
  };

  /** Norwegian edge-type labels. */
  const TYPE_LABELS = {
    ridge: "Møne",
    valley: "Gradrenne",
    eave: "Takfot / takrenne",
    hip: "Valmlinje",
    gable: "Vindskier",
    unknown: "Uklassifisert",
  };

  function toNumber(value) {
    if (typeof value === "number") return isNaN(value) ? 0 : value;
    if (typeof value === "string" && value.trim() !== "") {
      var parsed = parseFloat(value.replace(/\s/g, ""));
      if (!isNaN(parsed)) return parsed;
    }
    return 0;
  }

  function parseGeometry(geometryRaw) {
    if (!geometryRaw) return null;
    if (typeof geometryRaw === "object") return geometryRaw;
    if (typeof geometryRaw === "string") {
      try {
        return JSON.parse(geometryRaw);
      } catch (e) {
        console.warn("Failed to parse facet geometry", e, geometryRaw);
        return null;
      }
    }
    return null;
  }

  //split StreetAddress with coma
  var streetAddress = outputJson.StreetAddress || "";
  var streetAddressSplit = streetAddress.split(",");
  streetAddress = streetAddressSplit[0];

  // Build FullAddress from existing fields
  var fullAddressParts = [];
  if (streetAddress) fullAddressParts.push(streetAddress);
  var postalAndCity = [outputJson.PostalCode || "", outputJson.City || ""]
    .map(function (p) {
      return p.trim();
    })
    .filter(function (p) {
      return p.length > 0;
    })
    .join(" ");
  if (postalAndCity) fullAddressParts.push(postalAndCity);
  var fullAddress = fullAddressParts.join(", ");

  function buildGroundZLookup(buildingsHeights, byggId) {
    var entry = buildingsHeights.find(function (b) {
      return String(b.ByggId) === String(byggId);
    });
    if (!entry || !entry.body || !Array.isArray(entry.body.Punkter)) return {};
    var lookup = {};
    entry.body.Punkter.forEach(function (p) {
      if (
        typeof p.X === "number" &&
        typeof p.Y === "number" &&
        typeof p.Z === "number"
      ) {
        lookup[p.X.toFixed(9) + "|" + p.Y.toFixed(9)] = p.Z;
      }
    });
    return lookup;
  }

  function groundZForPoint(lon, lat, groundZLookup) {
    var key = lon.toFixed(9) + "|" + lat.toFixed(9);
    var z = groundZLookup[key];
    return typeof z === "number" && isFinite(z) ? z : null;
  }

  function groundZForEdge(edge, groundZLookup) {
    var zs = [];
    [edge.start, edge.end].forEach(function (p) {
      if (!p) return;
      var gz = groundZForPoint(p.lon, p.lat, groundZLookup);
      if (typeof gz === "number") zs.push(gz);
    });
    return zs.length
      ? zs.reduce(function (a, b) {
          return a + b;
        }, 0) / zs.length
      : null;
  }

  function geometryToRelativeZ(geometry, groundZLookup) {
    if (
      !geometry ||
      geometry.type !== "Polygon" ||
      !Array.isArray(geometry.coordinates)
    )
      return geometry;
    var ring = geometry.coordinates[0];
    if (!Array.isArray(ring) || ring.length < 4) return geometry;

    var groundCandidates = [];
    for (var i = 0; i < ring.length; i++) {
      var p = ring[i];
      if (!Array.isArray(p) || p.length < 2) continue;
      var gz = groundZForPoint(p[0], p[1], groundZLookup);
      if (typeof gz === "number") groundCandidates.push(gz);
    }
    var baseGroundZ = groundCandidates.length
      ? Math.min.apply(null, groundCandidates)
      : null;

    var newRing = ring.map(function (p) {
      if (!Array.isArray(p) || p.length < 3) return p;
      var lon = p[0],
        lat = p[1],
        zAbs = p[2];
      if (
        typeof lon !== "number" ||
        typeof lat !== "number" ||
        typeof zAbs !== "number"
      )
        return p;
      var gz = groundZForPoint(lon, lat, groundZLookup);
      var useGround = typeof gz === "number" ? gz : baseGroundZ;
      if (typeof useGround === "number") return [lon, lat, zAbs - useGround];
      return [lon, lat, zAbs]; // safe fallback
    });
    var result = {};
    for (var k in geometry) {
      if (geometry.hasOwnProperty(k)) result[k] = geometry[k];
    }
    result.coordinates = [newRing];
    return result;
  }

  function mapBuildingTypeGroup(buildingTypeCode) {
    var code = typeof buildingTypeCode === "number" ? buildingTypeCode : null;
    if (code === null || code === 999) return "Ukjent";
    if (code === 111 || code === 112 || code === 113) return "Enebolig";
    if (code === 121 || code === 122 || code === 123 || code === 124)
      return "Tomannsbolig";
    if (code === 131 || code === 133 || code === 135 || code === 136)
      return "Rekkehus";
    if (
      (code >= 141 && code <= 146) ||
      code === 151 ||
      code === 152 ||
      code === 159 ||
      code === 193 ||
      code === 199 ||
      code === 523
    )
      return "Boligblokk";
    if (code === 181 || code === 439) return "Garasje";
    if (code === 182) return "Anneks";
    if (code === 183 || code === 245) return "Uthus";
    if (
      code === 161 ||
      code === 162 ||
      code === 163 ||
      code === 171 ||
      code === 172
    )
      return "Fritidsbolig";
    if (
      (code >= 211 && code <= 299) ||
      (code >= 311 && code <= 399) ||
      (code >= 521 && code <= 539) ||
      code === 330 ||
      code === 429 ||
      code === 431
    )
      return "Næringsbygg";
    if (
      (code >= 611 && code <= 699) ||
      (code >= 719 && code <= 739) ||
      (code >= 821 && code <= 829) ||
      code === 830 ||
      code === 840
    )
      return "Offentlig bygg";
    if (
      code === 241 ||
      code === 243 ||
      code === 244 ||
      code === 248 ||
      code === 249
    )
      return "Landbruksbygg";
    return "Ukjent";
  }

  var _DEG2RAD = Math.PI / 180;

  function degToRad(d) {
    return d * _DEG2RAD;
  }

  /**
   * Equirectangular projection — lon/lat → local (East, North) metres.
   * Accurate to < 0.1 % for distances under 1 km (roof scale).
   */
  function llToMetric(lon, lat, lon0, lat0) {
    var cosLat0 = Math.cos(lat0 * _DEG2RAD);
    return {
      x: (lon - lon0) * _DEG2RAD * CFG.EARTH_R * cosLat0,
      y: (lat - lat0) * _DEG2RAD * CFG.EARTH_R,
    };
  }

  function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
  }

  // --- Vector-3 operations (all pure, no mutation) ---
  function sub3(a, b) {
    return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
  }
  function scale3(a, s) {
    return [a[0] * s, a[1] * s, a[2] * s];
  }
  function dot3(a, b) {
    return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  }
  function cross3(a, b) {
    return [
      a[1] * b[2] - a[2] * b[1],
      a[2] * b[0] - a[0] * b[2],
      a[0] * b[1] - a[1] * b[0],
    ];
  }
  function len3(a) {
    return Math.hypot(a[0], a[1], a[2]);
  }
  function normalize3(a) {
    var L = len3(a);
    return L > 1e-12 ? [a[0] / L, a[1] / L, a[2] / L] : [0, 0, 1];
  }

  function dot2(ax, ay, bx, by) {
    return ax * bx + ay * by;
  }

  /**
   * Ray-casting point-in-polygon (2D XY of metric coords).
   * ring elements are [mx, my, (mz)].
   */
  function pointInPolygon2D(px, py, ring) {
    var inside = false;
    for (var i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      var xi = ring[i][0],
        yi = ring[i][1];
      var xj = ring[j][0],
        yj = ring[j][1];
      if (
        yi > py !== yj > py &&
        px < ((xj - xi) * (py - yi)) / (yj - yi) + xi
      ) {
        inside = !inside;
      }
    }
    return inside;
  }

  /**
   * Newell method — robust polygon-normal from 3-D metric ring.
   * Works for arbitrary (non-convex) near-planar polygons.
   * Returns { x, y, z } with z ≥ 0  (upward-facing).          (§2)
   */
  function newellNormal(pts) {
    var nx = 0,
      ny = 0,
      nz = 0;
    var n = pts.length;
    for (var i = 0; i < n; i++) {
      var c = pts[i];
      var next = pts[(i + 1) % n];
      nx += (c[1] - next[1]) * (c[2] + next[2]);
      ny += (c[2] - next[2]) * (c[0] + next[0]);
      nz += (c[0] - next[0]) * (c[1] + next[1]);
    }
    var L = Math.hypot(nx, ny, nz);
    if (L < 1e-12) return null;
    // Force upward orientation (§2: "flip if n.z < 0")
    if (nz < 0) {
      nx = -nx;
      ny = -ny;
      nz = -nz;
    }
    return { x: nx / L, y: ny / L, z: nz / L };
  }

  /**
   * Evaluate the plane height z at (x, y) given normal n and reference point p0.
   * Requires n.z ≠ 0  (non-vertical plane — true for all valid roof facets).
   */
  function planeZAt(x, y, norm, p0) {
    if (Math.abs(norm.z) < 1e-12) return p0[2];
    return p0[2] - (norm.x * (x - p0[0]) + norm.y * (y - p0[1])) / norm.z;
  }

  /**
   * Centroid of a 3-D point set (simple average).
   */
  function centroid3D(pts) {
    var sx = 0,
      sy = 0,
      sz = 0,
      n = pts.length;
    for (var i = 0; i < n; i++) {
      sx += pts[i][0];
      sy += pts[i][1];
      sz += pts[i][2];
    }
    return [sx / n, sy / n, sz / n];
  }

  /**
   * Steepest-descent direction in the XY plane from a facet normal.  (§4.1)
   * g = normalize( -nx/nz , -ny/nz )
   */
  function downslopeDir(norm) {
    if (!norm || Math.abs(norm.z) < 1e-12) return { x: 0, y: 0 };
    var gx = -norm.x / norm.z;
    var gy = -norm.y / norm.z;
    var L = Math.hypot(gx, gy);
    return L > 1e-12 ? { x: gx / L, y: gy / L } : { x: 0, y: 0 };
  }

  /**
   * Build an internal facet with both lon/lat AND metric coordinates,
   * plus the Newell plane normal computed from metric 3-D vertices.
   */
  function buildInternalFacet(facet, buildingId, lon0, lat0) {
    if (!facet.Geometry || facet.Geometry.type !== "Polygon") return null;
    var ring = facet.Geometry.coordinates[0];
    if (!Array.isArray(ring) || ring.length < 4) return null;

    var zs = [];
    for (var i = 0; i < ring.length; i++) {
      var z = ring[i][2];
      if (typeof z === "number") zs.push(z);
    }
    if (!zs.length) return null;

    var zMin = Math.min.apply(null, zs);
    var zMax = Math.max.apply(null, zs);
    var zAvg = 0;
    for (var j = 0; j < zs.length; j++) zAvg += zs[j];
    zAvg /= zs.length;

    // Step 0 — convert to metric [mx, my, z]
    var coordsMetric = [];
    for (var k = 0; k < ring.length; k++) {
      var m = llToMetric(ring[k][0], ring[k][1], lon0, lat0);
      coordsMetric.push([
        m.x,
        m.y,
        typeof ring[k][2] === "number" ? ring[k][2] : 0,
      ]);
    }

    // Strip closing duplicate for Newell computation
    var isClosed =
      ring.length >= 2 &&
      Math.abs(ring[0][0] - ring[ring.length - 1][0]) < 1e-10 &&
      Math.abs(ring[0][1] - ring[ring.length - 1][1]) < 1e-10;
    var openMetric = isClosed ? coordsMetric.slice(0, -1) : coordsMetric;

    // Step 2 — compute plane normal via Newell method
    var normal = newellNormal(openMetric);

    // Reference point for plane evaluation (centroid minimises error)
    var refPt = centroid3D(openMetric);

    return {
      buildingId: buildingId,
      facetId: facet.id,
      slopeDeg: facet.Slope,
      aspectDeg: facet.Direction,
      coordsLLZ: ring,
      coordsMetric: coordsMetric,
      openMetric: openMetric,
      normal: normal,
      refPt: refPt,
      zMin: zMin,
      zMax: zMax,
      zAvg: zAvg,
      deltaZ: zMax - zMin,
      area2D: facet.Area,
    };
  }

  function isFacetValid(f) {
    if (f.area2D < CFG.MIN_FACET_AREA_M2) return false;
    if (f.slopeDeg > CFG.MAX_FACET_SLOPE_DEG) return false;
    if (f.deltaZ < CFG.MIN_DZ_SMALL && f.area2D < CFG.SMALL_AREA_M2)
      return false;
    return true;
  }

  function snapM(v) {
    return Math.round(v / CFG.SNAP_M) * CFG.SNAP_M;
  }

  /**
   * Build edge adjacency map from metric coordinates.
   * key  →  { segments: [ { facetId, startLL, endLL, startM, endM }, … ] }
   *
   * 2 segments ⇒ internal (ridge / hip / valley candidate)
   * 1 segment  ⇒ boundary (eave / gable candidate)
   */
  function buildEdgeMap(internalFacets) {
    var map = {};

    for (var fi = 0; fi < internalFacets.length; fi++) {
      var f = internalFacets[fi];
      var mPs = f.coordsMetric;
      var llPs = f.coordsLLZ;

      for (var i = 0; i < mPs.length - 1; i++) {
        var mx1 = mPs[i][0],
          my1 = mPs[i][1],
          mz1 = mPs[i][2];
        var mx2 = mPs[i + 1][0],
          my2 = mPs[i + 1][1],
          mz2 = mPs[i + 1][2];

        // Discard degenerate / micro edges
        if (Math.hypot(mx2 - mx1, my2 - my1) < CFG.MIN_EDGE_M) continue;

        // Snapped metric key for dedup
        var a =
          snapM(mx1) + "," + snapM(my1) + "," + snapM(mx2) + "," + snapM(my2);
        var b =
          snapM(mx2) + "," + snapM(my2) + "," + snapM(mx1) + "," + snapM(my1);
        var key = a < b ? a : b;

        if (!map[key]) map[key] = { segments: [] };

        map[key].segments.push({
          facetId: f.facetId,
          startLL: { lon: llPs[i][0], lat: llPs[i][1], z: llPs[i][2] || 0 },
          endLL: {
            lon: llPs[i + 1][0],
            lat: llPs[i + 1][1],
            z: llPs[i + 1][2] || 0,
          },
          startM: [mx1, my1, mz1],
          endM: [mx2, my2, mz2],
        });
      }
    }
    return map;
  }

  // ---------------------------------------------------------------
  // §3.1  Crest vs Trough inside-point sampling
  // ---------------------------------------------------------------
  /**
   * For each adjacent facet:
   *   • ti = normalize( cross(ni, edgeDir) )   — in-plane ⊥ to edge
   *   • Choose sign of ti that points INSIDE the polygon (PIP test)
   *   • Evaluate plane height at that inside point  → z_in
   *   • Compare to plane height at edge midpoint    → z_edge
   *
   * Both DOWN  →  "crest"   (Ridge or Hip)
   * Both UP    →  "trough"  (Valley)
   * Else       →  "ambiguous"
   */
  function crestOrTroughTest(startM, endM, f1, f2) {
    if (!f1.normal || !f2.normal) return "ambiguous";

    // Edge direction (3-D metric)
    var eDir = normalize3(sub3(endM, startM));

    // Midpoint
    var mx = (startM[0] + endM[0]) * 0.5;
    var my = (startM[1] + endM[1]) * 0.5;

    var deltas = []; // one per facet

    var facets = [f1, f2];
    for (var fi = 0; fi < 2; fi++) {
      var f = facets[fi];
      var n = f.normal;

      // ti = cross( normal, edgeDir )  — lies in facet plane, ⊥ to edge
      var ti = cross3([n.x, n.y, n.z], eDir);
      var tiLen = len3(ti);
      if (tiLen < 1e-12) {
        deltas.push(0);
        continue;
      }

      // Normalise & project to XY for PIP
      var tiU = scale3(ti, 1 / tiLen);
      var eps = CFG.SAMPLE_EPS_M;

      var plusX = mx + eps * tiU[0],
        plusY = my + eps * tiU[1];
      var minusX = mx - eps * tiU[0],
        minusY = my - eps * tiU[1];

      var inPlus = pointInPolygon2D(plusX, plusY, f.coordsMetric);
      var inMinus = pointInPolygon2D(minusX, minusY, f.coordsMetric);

      var insideX, insideY;
      if (inPlus && !inMinus) {
        insideX = plusX;
        insideY = plusY;
      } else if (inMinus && !inPlus) {
        insideX = minusX;
        insideY = minusY;
      } else {
        // Both or neither — tie-break toward centroid
        var cx = f.refPt[0],
          cy = f.refPt[1];
        var dp = dot2(tiU[0], tiU[1], cx - mx, cy - my);
        if (dp >= 0) {
          insideX = plusX;
          insideY = plusY;
        } else {
          insideX = minusX;
          insideY = minusY;
        }
      }

      // Plane heights
      var zEdge = planeZAt(mx, my, n, f.refPt);
      var zIn = planeZAt(insideX, insideY, n, f.refPt);

      deltas.push(zIn - zEdge); // positive = inside goes UP
    }

    var tol = CFG.CREST_TOL_M;
    if (deltas[0] < -tol && deltas[1] < -tol) return "crest";
    if (deltas[0] > tol && deltas[1] > tol) return "trough";
    return "ambiguous";
  }

  // ---------------------------------------------------------------
  // §3.2  Dihedral magnitude (filter coplanar edges)
  // ---------------------------------------------------------------
  function dihedralDeg(n1, n2) {
    if (!n1 || !n2) return 0;
    var dp = clamp(n1.x * n2.x + n1.y * n2.y + n1.z * n2.z, -1, 1);
    return (Math.acos(dp) * 180) / Math.PI;
  }

  // ---------------------------------------------------------------
  // §3.3  Shared edge: Crest → Ridge vs Hip (+ aspect opposition)
  // ---------------------------------------------------------------
  function classifySharedEdge(seg, f1, f2) {
    var startM = seg.startM;
    var endM = seg.endM;
    var meta = { isBoundary: false };

    // Metric edge lengths
    var lenXY = Math.hypot(endM[0] - startM[0], endM[1] - startM[1]);
    var len3D = Math.hypot(
      endM[0] - startM[0],
      endM[1] - startM[1],
      endM[2] - startM[2],
    );

    // Noise gate: very short edges
    if (len3D < CFG.MIN_INTERNAL_EDGE_M) {
      return {
        type: "unknown",
        meta: { isBoundary: false, noise: true, noiseReason: "short_edge" },
      };
    }

    // Both facets nearly flat → not a critical line
    var s1 = typeof f1.slopeDeg === "number" ? f1.slopeDeg : 0;
    var s2 = typeof f2.slopeDeg === "number" ? f2.slopeDeg : 0;
    if (s1 < 3 && s2 < 3) {
      return {
        type: "unknown",
        meta: {
          isBoundary: false,
          flatSeam: true,
          flatSlopeDeg: { f1: s1, f2: s2 },
        },
      };
    }

    // ── Flat-facet transition ──
    // When ONE facet is nearly flat (ridge cap, flat strip, dormer pad) and
    // the other is a normal sloped facet, the crest/trough inside-sampling
    // becomes unreliable because small Z-noise on the flat surface is
    // amplified at the sample point. Fall back to height-ratio on the
    // sloped facet to determine hip vs valley.
    var flatThresh = CFG.FLAT_TRANSITION_SLOPE_DEG;
    var oneFlat =
      (s1 < flatThresh && s2 >= flatThresh) ||
      (s2 < flatThresh && s1 >= flatThresh);
    if (oneFlat) {
      var slopedF = s1 >= flatThresh ? f1 : f2;
      var flatF = s1 >= flatThresh ? f2 : f1;
      var edgeAvgZ = (startM[2] + endM[2]) * 0.5;
      var dz = slopedF.zMax - slopedF.zMin;
      var ratio = dz > 0.01 ? clamp((edgeAvgZ - slopedF.zMin) / dz, 0, 1) : 0.5;
      meta.flatTransition = true;
      meta.flatFacetSlope = flatF.slopeDeg;
      meta.slopedFacetRatio = ratio;

      // Short flat-transition edges are noise / connectors — not critical lines
      if (lenXY < 1.0) {
        meta.noise = true;
        meta.noiseReason = "short_flat_transition";
        return { type: "unknown", meta: meta };
      }

      // Determine hip vs valley by where the edge sits on the sloped facet.
      // Low ratio → the edge is at the bottom of the slope; water flows
      // from the sloped facet DOWN INTO the flat strip → valley (gutter).
      // High ratio → the edge is near the top; the flat section is a ridge
      // cap or dormer pad → hip (crest-to-flat transition).
      if (ratio < 0.5) {
        meta.valleyBy = "flat_transition_low";
        return { type: "valley", meta: meta };
      }
      meta.hipBy = "flat_transition";
      return { type: "hip", meta: meta };
    }

    // §3.2 — Dihedral magnitude filter
    var dih = dihedralDeg(f1.normal, f2.normal);
    meta.dihedralDeg = dih;

    if (dih < CFG.DIHEDRAL_COPLANAR_DEG) {
      meta.coplanar = true;
      return { type: "unknown", meta: meta };
    }

    // §3.1 — Crest / Trough / Ambiguous
    var fold = crestOrTroughTest(startM, endM, f1, f2);
    meta.fold = fold;

    // Edge slope along itself (rise / run)
    var edgeSlope = lenXY > 1e-6 ? Math.abs(endM[2] - startM[2]) / lenXY : 0;
    meta.edgeSlope = edgeSlope;

    // --- Crest → Ridge or Hip (§3.3) ---
    if (fold === "crest") {
      addAspectMeta(meta, f1, f2);
      if (edgeSlope < CFG.RIDGE_MAX_SLOPE) {
        meta.ridgeBy = "crest_horizontal";
        return { type: "ridge", meta: meta };
      }
      // Sloped crest: use aspect opposition to distinguish ridge from hip.
      // On hip roofs, ridge lines can slope significantly; the key signal is
      // that the two facets face roughly opposite directions.
      if (meta.opposing) {
        meta.ridgeBy = "crest_sloped_opposing";
        return { type: "ridge", meta: meta };
      }
      meta.hipBy = "crest_sloped";
      return { type: "hip", meta: meta };
    }

    // --- Trough → Valley ---
    if (fold === "trough") {
      meta.valleyBy = "trough_test";
      return { type: "valley", meta: meta };
    }

    // --- Ambiguous: resolve with secondary signals ---
    return resolveAmbiguousShared(startM, endM, f1, f2, edgeSlope, meta);
  }

  /**
   * Add facet-aspect opposition metadata (§3.3 "extra confidence").
   */
  function addAspectMeta(meta, f1, f2) {
    if (typeof f1.aspectDeg === "number" && typeof f2.aspectDeg === "number") {
      var a1 = degToRad(f1.aspectDeg),
        a2 = degToRad(f2.aspectDeg);
      var aspectDot = Math.sin(a1) * Math.sin(a2) + Math.cos(a1) * Math.cos(a2);
      meta.aspectDot = aspectDot;
      meta.opposing = aspectDot <= CFG.OPPOSING_DOT;
    }
  }

  /**
   * "Slope toward edge" water-flow test.
   *
   * For each facet, checks whether the facet's downslope direction (water flow)
   * has a component pointing TOWARD the shared edge.
   *
   * Method:
   *   1. Compute the edge's perpendicular in XY that points toward the facet centroid.
   *   2. Get the facet's downslope direction (from metadata Direction).
   *   3. dot(downslope, perpTowardFacet) < 0 → water flows toward edge (valley).
   *      dot(downslope, perpTowardFacet) > 0 → water flows away from edge (ridge).
   *
   * Returns { f1Toward: bool, f2Toward: bool, pattern: string }
   *   pattern: "both_toward" | "both_away" | "mixed" | "unclear"
   */
  function slopeTowardEdgeTest(startM, endM, f1, f2) {
    var mx = (startM[0] + endM[0]) * 0.5;
    var my = (startM[1] + endM[1]) * 0.5;

    // Edge direction unit vector in XY
    var edx = endM[0] - startM[0],
      edy = endM[1] - startM[1];
    var edL = Math.hypot(edx, edy);
    if (edL < 1e-8)
      return { f1Toward: false, f2Toward: false, pattern: "unclear" };
    var euX = edx / edL,
      euY = edy / edL;

    // Left perpendicular of edge: (-euY, euX)
    var perpLX = -euY,
      perpLY = euX;

    var result = { f1Toward: false, f2Toward: false, pattern: "unclear" };
    var facets = [f1, f2];
    var keys = ["f1Toward", "f2Toward"];
    var signs = [];

    for (var i = 0; i < 2; i++) {
      var f = facets[i];
      // Which side is the facet centroid on?
      var cx = f.refPt[0] - mx,
        cy = f.refPt[1] - my;
      var side = cx * perpLX + cy * perpLY; // positive = left side
      if (Math.abs(side) < 1e-8) {
        signs.push(0);
        continue;
      }

      // perpTowardFacet: perpendicular pointing FROM edge TOWARD facet centroid
      var ptfX = side > 0 ? perpLX : -perpLX;
      var ptfY = side > 0 ? perpLY : -perpLY;

      // Facet downslope direction (from metadata)
      var dsDir;
      if (typeof f.aspectDeg === "number") {
        var rad = (f.aspectDeg * Math.PI) / 180;
        dsDir = { x: Math.sin(rad), y: Math.cos(rad) };
      } else {
        dsDir = downslopeDir(f.normal);
      }

      // dot(downslope, perpTowardFacet):
      //   > 0 → water flows away from edge (toward facet interior)
      //   < 0 → water flows toward the edge
      var dp = dsDir.x * ptfX + dsDir.y * ptfY;
      if (dp < -0.05) {
        result[keys[i]] = true; // toward edge
        signs.push(-1);
      } else if (dp > 0.05) {
        result[keys[i]] = false; // away from edge
        signs.push(1);
      } else {
        signs.push(0); // parallel to edge
      }
    }

    if (signs[0] === -1 && signs[1] === -1) result.pattern = "both_toward";
    else if (signs[0] === 1 && signs[1] === 1) result.pattern = "both_away";
    else if (
      (signs[0] === -1 && signs[1] === 1) ||
      (signs[0] === 1 && signs[1] === -1)
    )
      result.pattern = "mixed";
    else result.pattern = "unclear";

    return result;
  }

  /**
   * Resolve "ambiguous" fold using centroid-Z comparison and aspect data.
   * This handles saddle-like edges and segmentation artefacts.
   */
  function resolveAmbiguousShared(startM, endM, f1, f2, edgeSlope, meta) {
    meta.ambiguousResolution = true;
    addAspectMeta(meta, f1, f2);

    // ── Water-flow test ──
    // The most physically meaningful signal: does water from each facet
    // flow toward or away from this edge?
    var flowTest = slopeTowardEdgeTest(startM, endM, f1, f2);
    meta.flowPattern = flowTest.pattern;
    meta.flowDetail = {
      f1Toward: flowTest.f1Toward,
      f2Toward: flowTest.f2Toward,
    };

    // Both facets slope toward the edge → water converges → valley
    if (flowTest.pattern === "both_toward") {
      meta.valleyBy = "both_slope_toward_edge";
      return { type: "valley", meta: meta };
    }

    // Both facets slope away from the edge:
    //   - opposing aspects (180° apart) → ridge (classic ridge-line)
    //   - non-opposing: check height position to distinguish hip from valley.
    //     On multi-hip roofs, hip lines radiate from a central peak — both
    //     adjacent facets slope away, but the edge sits near the TOP of both
    //     facets.  A true valley at a re-entrant corner sits near the BOTTOM.
    if (flowTest.pattern === "both_away") {
      if (meta.opposing) {
        if (edgeSlope < CFG.RIDGE_MAX_SLOPE) {
          meta.ridgeBy = "both_away_opposing_horizontal";
          return { type: "ridge", meta: meta };
        }
        meta.ridgeBy = "both_away_opposing_sloped";
        return { type: "ridge", meta: meta };
      }

      // Height-ratio check: how high is the edge on each facet?
      var edgeAvgZ = (startM[2] + endM[2]) * 0.5;
      var hrF1 =
        f1.zMax - f1.zMin > 0.1
          ? clamp((edgeAvgZ - f1.zMin) / (f1.zMax - f1.zMin), 0, 1)
          : 0.5;
      var hrF2 =
        f2.zMax - f2.zMin > 0.1
          ? clamp((edgeAvgZ - f2.zMin) / (f2.zMax - f2.zMin), 0, 1)
          : 0.5;
      var hrMin = Math.min(hrF1, hrF2);
      meta.bothAwayHR = { f1: hrF1, f2: hrF2, min: hrMin };

      // Edge near the top of both facets → convex hip crease (peak-radiating)
      if (hrMin > 0.3) {
        meta.hipBy = "both_away_high_on_facets";
        return { type: "hip", meta: meta };
      }

      // Edge near the bottom → likely re-entrant valley
      meta.valleyBy = "both_away_non_opposing";
      return { type: "valley", meta: meta };
    }

    // §6 — Signed dihedral as secondary signal
    var signedResult = signedDihedralScore(startM, endM, f1, f2);
    if (signedResult !== 0) {
      meta.signedDihedral = signedResult;
      if (signedResult > 0) {
        // Convex fold — but at L-junctions (perpendicular aspects) the
        // geometric "convex" reading is misleading; the junction is
        // physically a valley where water converges at the re-entrant corner.
        if (meta.opposing) {
          meta.ridgeBy =
            edgeSlope < CFG.RIDGE_MAX_SLOPE
              ? "ambiguous_signed_ridge_horizontal"
              : "ambiguous_signed_ridge_opposing";
          return { type: "ridge", meta: meta };
        }
        // Perpendicular aspects (|aspectDot| < 0.3) → L-junction valley
        if (
          typeof meta.aspectDot === "number" &&
          Math.abs(meta.aspectDot) < 0.3
        ) {
          meta.valleyBy = "signed_convex_perpendicular_junction";
          return { type: "valley", meta: meta };
        }
        meta.hipBy = "ambiguous_signed_hip";
        return { type: "hip", meta: meta };
      } else {
        meta.valleyBy = "ambiguous_signed_valley";
        return { type: "valley", meta: meta };
      }
    }

    // Centroid-Z comparison as final fallback
    var edgeAvgZ = (startM[2] + endM[2]) * 0.5;
    var tol = CFG.CREST_TOL_M;
    var cz1 = f1.zAvg;
    var cz2 = f2.zAvg;

    var aboveBoth = edgeAvgZ > cz1 + tol && edgeAvgZ > cz2 + tol;
    var belowBoth = edgeAvgZ < cz1 - tol && edgeAvgZ < cz2 - tol;

    meta.centroidZ = { f1: cz1, f2: cz2, edge: edgeAvgZ };

    if (aboveBoth) {
      if (edgeSlope < CFG.RIDGE_MAX_SLOPE || meta.opposing) {
        meta.ridgeBy =
          edgeSlope < CFG.RIDGE_MAX_SLOPE
            ? "ambiguous_above_horizontal"
            : "ambiguous_above_opposing";
        return { type: "ridge", meta: meta };
      }
      meta.hipBy = "ambiguous_above_sloped";
      return { type: "hip", meta: meta };
    }

    if (belowBoth) {
      meta.valleyBy = "ambiguous_below_centroids";
      return { type: "valley", meta: meta };
    }

    // True ambiguity: default to hip (safest geometric assignment)
    meta.hipBy = "ambiguous_default";
    return { type: "hip", meta: meta };
  }

  /**
   * §6 — Signed dihedral using triple product  e · (n1 × n2).
   * Returns +1 (convex / crest-like), -1 (concave / trough-like), or 0 (unclear).
   */
  function signedDihedralScore(startM, endM, f1, f2) {
    if (!f1.normal || !f2.normal) return 0;
    var n1 = f1.normal,
      n2 = f2.normal;

    // Edge direction in metric 3-D
    var eDir = [endM[0] - startM[0], endM[1] - startM[1], endM[2] - startM[2]];
    var eLen = len3(eDir);
    if (eLen < 1e-8) return 0;

    // Cross product n1 × n2
    var cx = n1.y * n2.z - n1.z * n2.y;
    var cy = n1.z * n2.x - n1.x * n2.z;
    var cz = n1.x * n2.y - n1.y * n2.x;

    // Triple product: eDir · (n1 × n2)
    var triple = eDir[0] * cx + eDir[1] * cy + eDir[2] * cz;

    // Disambiguate: determine which side f1 is on relative to the edge
    var emx = (startM[0] + endM[0]) * 0.5;
    var emy = (startM[1] + endM[1]) * 0.5;
    var elx = endM[0] - startM[0],
      ely = endM[1] - startM[1];
    var eL2 = Math.hypot(elx, ely) || 1;
    var leftNx = -ely / eL2,
      leftNy = elx / eL2;

    var c1 = f1.refPt;
    var sideC1 = (c1[0] - emx) * leftNx + (c1[1] - emy) * leftNy;
    var adjusted = sideC1 >= 0 ? triple : -triple;

    var threshold = 0.001 * eLen;
    if (adjusted > threshold) return +1; // convex → crest-like
    if (adjusted < -threshold) return -1; // concave → trough-like
    return 0;
  }

  // ---------------------------------------------------------------
  // §4  Boundary edges: Eave vs Gable
  // ---------------------------------------------------------------
  /**
   * Classify boundary edge using two strong signals (§4.2):
   *   (A) Edge "horizontalness"  s_edge = |Δz| / len_xy
   *   (B) Alignment with facet downslope  align = |dot(edgeDir, downslopeDir)|
   *
   * Eave:  s_edge small, align small (⊥ to slope), height near facet min
   * Gable: s_edge large or align large (∥ to slope)
   */
  function classifyBoundaryEdge(seg, facet) {
    var meta = { isBoundary: true };
    var sM = seg.startM,
      eM = seg.endM;

    // (A) Edge slope along itself
    var lenXY = Math.hypot(eM[0] - sM[0], eM[1] - sM[1]);
    var sEdge = lenXY > 1e-6 ? Math.abs(eM[2] - sM[2]) / lenXY : 0;
    meta.edgeSlope = sEdge;

    // (B) Downslope alignment — prefer metadata Direction (Norkart-computed)
    //     over Newell normal, since irregular vertex distributions can skew
    //     the Newell gradient.  Fall back to Newell if metadata is absent.
    var ds;
    if (typeof facet.aspectDeg === "number") {
      var rad = (facet.aspectDeg * Math.PI) / 180;
      ds = { x: Math.sin(rad), y: Math.cos(rad) };
    } else {
      ds = downslopeDir(facet.normal);
    }
    var edx = eM[0] - sM[0],
      edy = eM[1] - sM[1];
    var edL = Math.hypot(edx, edy) || 1;
    var eu = { x: edx / edL, y: edy / edL };
    var align = Math.abs(dot2(eu.x, eu.y, ds.x, ds.y));
    meta.downslopeAlign = align;

    // (C) Height relative to facet minimum
    var edgeMinZ = Math.min(sM[2], eM[2]);
    var dzFromMin = edgeMinZ - facet.zMin;
    meta.dzFromFacetMin = dzFromMin;

    // (C2) Height ratio: how high does the edge reach within the facet Z range?
    //      Low ratio = edge stays near the bottom (eave zone).
    var edgeMaxZ = Math.max(sM[2], eM[2]);
    var facetZRange = facet.zMax - facet.zMin;
    var heightRatio =
      facetZRange > 0.1 ? (edgeMaxZ - facet.zMin) / facetZRange : 0.5;
    meta.heightRatio = heightRatio;

    // ── Decision rules (§4.2) — ordered by signal strength ──

    // (0) FLAT-FACET BOUNDARY: when facet slope < 5°, the downslope
    //     direction is essentially arbitrary.  There is no meaningful
    //     slope direction, so all perimeter edges are drip-lines (eaves).
    if (facet.slopeDeg < CFG.FLAT_TRANSITION_SLOPE_DEG) {
      meta.eaveBy = "flat_facet_boundary";
      return { type: "eave", meta: meta };
    }

    // (1) STRONG GABLE: steep edge clearly aligned with slope direction
    //     Guard: the edge must reach into the upper portion of the facet OR
    //     be long enough.  Short edges that stay near the bottom are eave-
    //     line jogs (notches), not gables.
    //     Override: when alignment with slope is very high (>0.9) AND the
    //     edge is at least 0.5 m, it is unmistakably running up/down the
    //     slope — allow gable even if short and low (e.g. sides of a
    //     notch/indent in the perimeter).  Edges < 0.5 m with high
    //     alignment are just vertex connectors / noise, not real gables.
    var lowOnFacet =
      heightRatio <= CFG.GABLE_LOW_HR_THRESH && lenXY < CFG.GABLE_MIN_LEN_LOW;
    if (align > 0.9 && lenXY >= 0.5) lowOnFacet = false;
    if (
      align > CFG.GABLE_MIN_ALIGN &&
      sEdge > CFG.GABLE_MIN_EDGE_SLOPE &&
      !lowOnFacet
    ) {
      meta.gableBy = "strong_aligned_steep";
      return { type: "gable", meta: meta };
    }

    // (1.5) PERPENDICULAR TO SLOPE: edges with very low downslope alignment
    //       are contour-following regardless of edge slope or height.
    //       On hip roofs, diagonal eave edges have high edge slope but stay
    //       perpendicular to the facet's true slope direction.
    if (align < CFG.EAVE_MAX_ALIGN) {
      meta.eaveBy = "perpendicular_to_slope";
      return { type: "eave", meta: meta };
    }

    // (2) STRONG EAVE: low slope, perpendicular to slope, near facet minimum
    if (
      sEdge < CFG.EAVE_MAX_EDGE_SLOPE &&
      align < CFG.EAVE_MAX_ALIGN &&
      dzFromMin <= CFG.EAVE_Z_TOL_M
    ) {
      meta.eaveBy = "horizontal_perpendicular_low";
      return { type: "eave", meta: meta };
    }

    // (3) CONTOUR EAVE: perpendicular to slope with moderate edge slope
    //     (real eaves on noisy roofs aren't perfectly horizontal)
    if (
      align < CFG.EAVE_MAX_ALIGN &&
      sEdge < CFG.EAVE_MAX_SLOPE_SOFT &&
      dzFromMin <= CFG.EAVE_Z_TOL_SECONDARY_M
    ) {
      meta.eaveBy = "contour_perpendicular_moderate";
      return { type: "eave", meta: meta };
    }

    // (4) STEEP EDGE: clearly climbing → gable regardless of alignment
    //     Same low-on-facet guard as rule (1).
    if (sEdge > CFG.GABLE_STEEP_SLOPE && !lowOnFacet) {
      meta.gableBy = "steep_edge";
      return { type: "gable", meta: meta };
    }

    // (4.5) HIGH-ALIGNMENT GABLE: edge direction strongly follows the
    //       slope direction → gable regardless of absolute edge slope.
    //       On gentle facets, gable edges have low absolute slope but
    //       unmistakable alignment with the downslope direction.
    //       Same low-on-facet guard as rule (1).
    if (align > CFG.GABLE_MIN_ALIGN && !lowOnFacet) {
      meta.gableBy = "aligned_with_slope";
      return { type: "gable", meta: meta };
    }

    // (5) LOW EDGE: near facet minimum with moderate characteristics
    if (dzFromMin <= CFG.EAVE_Z_TOL_M) {
      meta.eaveBy = "fallback_low";
      return { type: "eave", meta: meta };
    }

    // (6) HIGH ALIGNMENT: edge follows slope direction
    if (align > 0.5) {
      meta.gableBy = "moderate_aligned";
      return { type: "gable", meta: meta };
    }

    // (7) DEFAULT: use height position
    if (dzFromMin <= CFG.EAVE_Z_TOL_SECONDARY_M) {
      meta.eaveBy = "fallback_moderate_height";
      return { type: "eave", meta: meta };
    }

    meta.gableBy = "fallback_default";
    return { type: "gable", meta: meta };
  }

  // ---------------------------------------------------------------
  // Orchestrator: classify every edge in the map
  // ---------------------------------------------------------------
  function classifyAllEdges(edgeMap, internalFacets) {
    var facetById = {};
    for (var i = 0; i < internalFacets.length; i++) {
      facetById[internalFacets[i].facetId] = internalFacets[i];
    }

    var edges = [];
    var idx = 0;

    var keys = Object.keys(edgeMap);
    for (var ki = 0; ki < keys.length; ki++) {
      var segs = edgeMap[keys[ki]].segments;
      if (!segs.length) continue;

      var isBoundary = segs.length === 1;

      // ── Z-gap detection ──
      // Two segments can share the same 2D edge (lon/lat match) but be
      // at different heights in 3D.  This happens when adjacent facets
      // project onto the same 2D footprint but don't actually touch
      // in 3D (e.g. upper roof eave sitting above a lower facet eave).
      // If the max Z-difference at either vertex exceeds SHARED_Z_GAP_M,
      // treat each segment as a separate boundary edge.
      var zGapSplit = false;
      if (segs.length >= 2) {
        var seg0 = segs[0],
          seg1 = segs[1];
        // Match vertices: seg0.start may correspond to seg1.start or seg1.end
        // (edges can be oriented in opposite directions)
        var d_ss = Math.hypot(
          seg0.startM[0] - seg1.startM[0],
          seg0.startM[1] - seg1.startM[1],
        );
        var d_se = Math.hypot(
          seg0.startM[0] - seg1.endM[0],
          seg0.startM[1] - seg1.endM[1],
        );
        var zGap;
        if (d_ss < d_se) {
          // Same orientation: start↔start, end↔end
          zGap = Math.max(
            Math.abs(seg0.startM[2] - seg1.startM[2]),
            Math.abs(seg0.endM[2] - seg1.endM[2]),
          );
        } else {
          // Reversed orientation: start↔end, end↔start
          zGap = Math.max(
            Math.abs(seg0.startM[2] - seg1.endM[2]),
            Math.abs(seg0.endM[2] - seg1.startM[2]),
          );
        }
        if (zGap > CFG.SHARED_Z_GAP_M) {
          zGapSplit = true;
        }
      }

      // If Z-gap: facets share the same 2D edge but differ in Z — they
      // don't actually touch in 3D.  Classify via boundary rules of the
      // primary (largest-area) facet rather than blindly forcing eave.
      if (zGapSplit) {
        var s0 = segs[0];
        var start = s0.startLL;
        var end = s0.endLL;
        var lengthMeters = haversineMeters(
          start.lon,
          start.lat,
          end.lon,
          end.lat,
        );
        var allZ = [];
        for (var si = 0; si < segs.length; si++) {
          allZ.push(segs[si].startLL.z, segs[si].endLL.z);
        }
        var heightMin = Math.min.apply(null, allZ);
        var heightMax = Math.max.apply(null, allZ);
        var heightSum = 0;
        for (var zi = 0; zi < allZ.length; zi++) heightSum += allZ[zi];
        var heightAvg = heightSum / allZ.length;

        // Pick the primary facet (largest area) and classify as boundary
        var primarySeg = segs[0];
        var primaryFacet = facetById[segs[0].facetId];
        for (var si = 1; si < segs.length; si++) {
          var candFacet = facetById[segs[si].facetId];
          if (
            candFacet &&
            (!primaryFacet || candFacet.area2D > primaryFacet.area2D)
          ) {
            primarySeg = segs[si];
            primaryFacet = candFacet;
          }
        }
        var zGapResult = primaryFacet
          ? classifyBoundaryEdge(primarySeg, primaryFacet)
          : { type: "eave", meta: { eaveBy: "z_gap_drip_line" } };
        zGapResult.meta.zGapSplit = true;

        edges.push({
          id: "edge_" + idx,
          facetIds: uniqueFacetIds(segs),
          type: zGapResult.type,
          typeLabelNo: TYPE_LABELS[zGapResult.type] || TYPE_LABELS.unknown,
          heightAvg: heightAvg,
          heightMin: heightMin,
          heightMax: heightMax,
          start: start,
          end: end,
          lengthMeters: lengthMeters,
          meta: zGapResult.meta,
        });
        idx++;
        continue;
      }

      // Heights
      var allZ = [];
      for (var si = 0; si < segs.length; si++) {
        allZ.push(segs[si].startLL.z, segs[si].endLL.z);
      }
      var heightMin = Math.min.apply(null, allZ);
      var heightMax = Math.max.apply(null, allZ);
      var heightSum = 0;
      for (var zi = 0; zi < allZ.length; zi++) heightSum += allZ[zi];
      var heightAvg = heightSum / allZ.length;

      // Primary segment for output coords
      var s0 = segs[0];
      var start = s0.startLL;
      var end = s0.endLL;
      var lengthMeters = haversineMeters(
        start.lon,
        start.lat,
        end.lon,
        end.lat,
      );

      // Classify
      var result;
      if (isBoundary) {
        var fb = facetById[s0.facetId];
        result = fb
          ? classifyBoundaryEdge(s0, fb)
          : { type: "unknown", meta: { isBoundary: true } };
      } else if (segs.length >= 2) {
        var f1 = facetById[segs[0].facetId];
        var f2 = facetById[segs[1].facetId];
        result =
          f1 && f2
            ? classifySharedEdge(segs[0], f1, f2)
            : {
                type: "unknown",
                meta: { isBoundary: false, missingFacet: true },
              };
      } else {
        result = { type: "unknown", meta: {} };
      }

      edges.push({
        id: "edge_" + idx,
        facetIds: uniqueFacetIds(segs),
        type: result.type,
        typeLabelNo: TYPE_LABELS[result.type] || TYPE_LABELS.unknown,
        heightAvg: heightAvg,
        heightMin: heightMin,
        heightMax: heightMax,
        start: start,
        end: end,
        lengthMeters: lengthMeters,
        meta: result.meta,
      });
      idx++;
    }
    return edges;
  }

  function uniqueFacetIds(segs) {
    var seen = {};
    var result = [];
    for (var i = 0; i < segs.length; i++) {
      var fid = segs[i].facetId;
      if (!seen[fid]) {
        seen[fid] = true;
        result.push(fid); // preserve original type (number)
      }
    }
    return result;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // SECTION J — POST-PROCESSING  (Algorithm Step 5)
  //
  // 1. Tiny facets already dropped via isFacetValid
  // 2. Merge collinear contiguous boundary edges into single edges
  // 3. Graph-constraint soft checks (metadata only)
  // 4. Re-label short "island" edges via neighbour majority vote
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * §5.2 — Merge collinear contiguous boundary edges.
   *
   * Norkart polygons sometimes split one straight physical edge into
   * multiple segments (one per polygon vertex pair).  This pass chains
   * boundary segments that are:
   *   (a) same type  (b) same facetIds  (c) contiguous (shared vertex)
   *   (d) nearly collinear (turn angle < COLLINEAR_MAX_ANGLE_DEG)
   * into a single output edge.
   */
  function mergeCollinearBoundaryEdges(edges) {
    var maxAngle = CFG.COLLINEAR_MAX_ANGLE_DEG;

    function vKey(lon, lat) {
      return Math.round(lon * 1e7) + "," + Math.round(lat * 1e7);
    }

    function shareFacetId(a, b) {
      for (var i = 0; i < a.length; i++) {
        for (var j = 0; j < b.length; j++) {
          if (String(a[i]) === String(b[j])) return true;
        }
      }
      return false;
    }

    function turnAngleDeg(dx1, dy1, dx2, dy2) {
      var cross = dx1 * dy2 - dy1 * dx2;
      var dot = dx1 * dx2 + dy1 * dy2;
      return Math.abs(Math.atan2(cross, dot)) * 180 / Math.PI;
    }

    // Build start-vertex → index and end-vertex → index lookups
    var byStart = {}; // vKey → [idx]
    var byEnd = {};   // vKey → [idx]
    for (var i = 0; i < edges.length; i++) {
      var e = edges[i];
      var sk = vKey(e.start.lon, e.start.lat);
      var ek = vKey(e.end.lon, e.end.lat);
      if (!byStart[sk]) byStart[sk] = [];
      if (!byEnd[ek]) byEnd[ek] = [];
      byStart[sk].push(i);
      byEnd[ek].push(i);
    }

    var used = {};
    var merged = [];

    for (var i = 0; i < edges.length; i++) {
      if (used[i]) continue;
      used[i] = true;
      var e = edges[i];

      // Only merge boundary edges with a definite type
      if (!e.meta || !e.meta.isBoundary || e.type === "unknown") {
        merged.push(e);
        continue;
      }

      // Build a chain: extend forward then backward
      var chain = [i];

      // Extend forward (from chain tail's end vertex)
      var tailIdx = i;
      while (true) {
        var tail = edges[tailIdx];
        var ek = vKey(tail.end.lon, tail.end.lat);
        var cands = byStart[ek] || [];
        var found = false;
        for (var ci = 0; ci < cands.length; ci++) {
          var j = cands[ci];
          if (used[j]) continue;
          var c = edges[j];
          if (!c.meta || !c.meta.isBoundary) continue;
          if (c.type !== e.type) continue;
          if (!shareFacetId(c.facetIds, e.facetIds)) continue;
          var ang = turnAngleDeg(
            tail.end.lon - tail.start.lon, tail.end.lat - tail.start.lat,
            c.end.lon - c.start.lon, c.end.lat - c.start.lat
          );
          if (ang < maxAngle) {
            chain.push(j);
            used[j] = true;
            tailIdx = j;
            found = true;
            break;
          }
        }
        if (!found) break;
      }

      // Extend backward (from chain head's start vertex)
      var headIdx = i;
      while (true) {
        var head = edges[headIdx];
        var sk = vKey(head.start.lon, head.start.lat);
        var cands = byEnd[sk] || [];
        var found = false;
        for (var ci = 0; ci < cands.length; ci++) {
          var j = cands[ci];
          if (used[j]) continue;
          var c = edges[j];
          if (!c.meta || !c.meta.isBoundary) continue;
          if (c.type !== e.type) continue;
          if (!shareFacetId(c.facetIds, e.facetIds)) continue;
          var ang = turnAngleDeg(
            c.end.lon - c.start.lon, c.end.lat - c.start.lat,
            head.end.lon - head.start.lon, head.end.lat - head.start.lat
          );
          if (ang < maxAngle) {
            chain.unshift(j);
            used[j] = true;
            headIdx = j;
            found = true;
            break;
          }
        }
        if (!found) break;
      }

      // Single segment — keep as-is
      if (chain.length === 1) {
        merged.push(e);
        continue;
      }

      // Merge the chain into one edge
      var first = edges[chain[0]];
      var last = edges[chain[chain.length - 1]];
      var totalLen = 0;
      var hMin = Infinity, hMax = -Infinity;
      var mergedIds = [];
      for (var ci = 0; ci < chain.length; ci++) {
        var ce = edges[chain[ci]];
        totalLen += ce.lengthMeters;
        if (ce.heightMin < hMin) hMin = ce.heightMin;
        if (ce.heightMax > hMax) hMax = ce.heightMax;
        mergedIds.push(ce.id);
      }

      // Collect union of all facetIds from the chain
      var mergedFacetIdSet = {};
      for (var ci2 = 0; ci2 < chain.length; ci2++) {
        var ceFids = edges[chain[ci2]].facetIds;
        for (var fi = 0; fi < ceFids.length; fi++) {
          mergedFacetIdSet[ceFids[fi]] = true;
        }
      }
      var mergedFacetIds = Object.keys(mergedFacetIdSet).map(Number);

      var newMeta = {};
      for (var mk in first.meta) {
        if (first.meta.hasOwnProperty(mk)) newMeta[mk] = first.meta[mk];
      }
      newMeta.mergedSegments = chain.length;
      newMeta.mergedFrom = mergedIds;

      merged.push({
        id: null,
        facetIds: mergedFacetIds,
        type: first.type,
        typeLabelNo: first.typeLabelNo,
        heightAvg: (first.start.z + last.end.z) / 2,
        heightMin: hMin,
        heightMax: hMax,
        start: first.start,
        end: last.end,
        lengthMeters: totalLen,
        meta: newMeta,
      });
    }

    // Re-assign sequential IDs
    for (var i = 0; i < merged.length; i++) {
      merged[i].id = "edge_" + i;
    }
    return merged;
  }

  function postProcessEdges(edges, internalFacets) {
    // §5.2 — Merge collinear boundary edges first
    edges = mergeCollinearBoundaryEdges(edges);

    // --- Build vertex → edge-index adjacency ---
    var vtxEdges = {};
    function vKey(lon, lat) {
      return Math.round(lon * 1e7) + "," + Math.round(lat * 1e7);
    }

    for (var i = 0; i < edges.length; i++) {
      var e = edges[i];
      var k1 = vKey(e.start.lon, e.start.lat);
      var k2 = vKey(e.end.lon, e.end.lat);
      if (!vtxEdges[k1]) vtxEdges[k1] = [];
      if (!vtxEdges[k2]) vtxEdges[k2] = [];
      vtxEdges[k1].push(i);
      vtxEdges[k2].push(i);
    }

    function neighbours(idx) {
      var e = edges[idx];
      var k1 = vKey(e.start.lon, e.start.lat);
      var k2 = vKey(e.end.lon, e.end.lat);
      var out = {};
      var lists = [vtxEdges[k1], vtxEdges[k2]];
      for (var li = 0; li < lists.length; li++) {
        var arr = lists[li];
        if (!arr) continue;
        for (var j = 0; j < arr.length; j++) {
          if (arr[j] !== idx) out[arr[j]] = true;
        }
      }
      return Object.keys(out).map(Number);
    }

    // §5.4 — Re-label short isolated edges via majority vote
    //         (only for non-boundary shared edges; boundary type is definitive)
    for (var ri = 0; ri < edges.length; ri++) {
      var re = edges[ri];
      if (re.type === "unknown") continue;
      if (re.meta && re.meta.isBoundary) continue; // never override boundary classification
      if (re.meta && (re.meta.noise || re.meta.flatSeam || re.meta.coplanar))
        continue;
      if (re.lengthMeters > CFG.MIN_ISLAND_M) continue;

      var nbs = neighbours(ri);
      if (!nbs.length) continue;

      var votes = {};
      for (var ni = 0; ni < nbs.length; ni++) {
        var nt = edges[nbs[ni]].type;
        if (nt === "unknown") continue;
        votes[nt] = (votes[nt] || 0) + 1;
      }

      var bestType = null,
        bestCount = 0;
      for (var t in votes) {
        if (votes.hasOwnProperty(t) && votes[t] > bestCount) {
          bestCount = votes[t];
          bestType = t;
        }
      }

      if (bestType && bestType !== re.type && bestCount >= 2) {
        re.meta.relabeledFrom = re.type;
        re.meta.relabeledBy = "island_majority_vote";
        re.type = bestType;
        re.typeLabelNo = TYPE_LABELS[bestType] || TYPE_LABELS.unknown;
      }
    }

    // §5.5 — Dormer cheek valley detection
    //   When a small facet's boundary gable edge projects (2D) onto a larger
    //   facet, and the edge has significant alignment with the larger facet's
    //   downslope direction, it follows the intersection line of the two
    //   roof planes → dormer cheek valley (water converges from both slopes).
    if (internalFacets && internalFacets.length > 1) {
      // Build facetId → internalFacet lookup
      var facetById = {};
      for (var di = 0; di < internalFacets.length; di++) {
        facetById[internalFacets[di].facetId] = internalFacets[di];
      }

      for (var dj = 0; dj < edges.length; dj++) {
        var de = edges[dj];
        // Dormer cheek valleys are most often gables, but some ambiguous
        // cases are initially tagged as fallback-low eaves.
        var isGableCandidate = de.type === "gable";
        var isFallbackEaveCandidate =
          de.type === "eave" && de.meta && de.meta.eaveBy === "fallback_low";
        if (!isGableCandidate && !isFallbackEaveCandidate) continue;
        if (!de.meta || !de.meta.isBoundary) continue;
        if (de.lengthMeters < CFG.DORMER_MIN_LEN_M) continue;
        if (!de.facetIds || de.facetIds.length !== 1) continue;

        var ownFacet = facetById[de.facetIds[0]];
        if (!ownFacet) continue;

        // Find the ring segment of ownFacet matching this edge (lon/lat → metric)
        var sMet = null,
          eMet = null;
        for (var dm = 0; dm < ownFacet.coordsLLZ.length - 1; dm++) {
          var pA = ownFacet.coordsLLZ[dm];
          var pB = ownFacet.coordsLLZ[dm + 1];
          var matchFwd =
            Math.abs(pA[0] - de.start.lon) < 1e-7 &&
            Math.abs(pA[1] - de.start.lat) < 1e-7 &&
            Math.abs(pB[0] - de.end.lon) < 1e-7 &&
            Math.abs(pB[1] - de.end.lat) < 1e-7;
          var matchRev =
            Math.abs(pB[0] - de.start.lon) < 1e-7 &&
            Math.abs(pB[1] - de.start.lat) < 1e-7 &&
            Math.abs(pA[0] - de.end.lon) < 1e-7 &&
            Math.abs(pA[1] - de.end.lat) < 1e-7;
          if (matchFwd) {
            sMet = ownFacet.coordsMetric[dm];
            eMet = ownFacet.coordsMetric[dm + 1];
            break;
          }
          if (matchRev) {
            sMet = ownFacet.coordsMetric[dm + 1];
            eMet = ownFacet.coordsMetric[dm];
            break;
          }
        }
        if (!sMet || !eMet) continue;

        var midX = (sMet[0] + eMet[0]) * 0.5;
        var midY = (sMet[1] + eMet[1]) * 0.5;

        // Edge unit direction in 2D metric
        var edx = eMet[0] - sMet[0],
          edy = eMet[1] - sMet[1];
        var edLen = Math.hypot(edx, edy) || 1;
        var eUx = edx / edLen,
          eUy = edy / edLen;

        // Check each larger facet for projection
        for (var dl = 0; dl < internalFacets.length; dl++) {
          var largeFacet = internalFacets[dl];
          if (largeFacet.facetId === ownFacet.facetId) continue;
          if (largeFacet.area2D < ownFacet.area2D) continue;
          if (ownFacet.area2D / largeFacet.area2D > CFG.DORMER_AREA_RATIO)
            continue;

          // Point-in-polygon: does edge midpoint fall within the large facet?
          if (!pointInPolygon2D(midX, midY, largeFacet.coordsMetric)) continue;

          // Compute large facet's downslope direction (kept for metadata)
          var lfDs;
          if (typeof largeFacet.aspectDeg === "number") {
            var lfRad = (largeFacet.aspectDeg * Math.PI) / 180;
            lfDs = { x: Math.sin(lfRad), y: Math.cos(lfRad) };
          } else {
            lfDs = downslopeDir(largeFacet.normal);
          }

          // Alignment of edge with the large facet's slope
          var mainAlign = Math.abs(dot2(eUx, eUy, lfDs.x, lfDs.y));

          if (mainAlign >= CFG.DORMER_MAIN_ALIGN) {
            de.meta.relabeledFrom = de.type;
            de.meta.valleyBy = "dormer_cheek";
            de.meta.dormerMainFacet = largeFacet.facetId;
            de.meta.dormerMainAlign = mainAlign;
            // Promote to shared edge: this boundary edge is physically
            // the intersection of the dormer plane and the main roof plane
            de.meta.isBoundary = false;
            if (de.facetIds.indexOf(largeFacet.facetId) < 0) {
              de.facetIds.push(largeFacet.facetId);
            }
            de.type = "valley";
            de.typeLabelNo = TYPE_LABELS.valley;
            break; // found a matching large facet
          }
        }
      }
    }

    // §5.3 — Graph-constraint soft checks (metadata annotations only)
    // Ridge polylines should connect to hips/gables; valleys should drain to eaves
    for (var gi = 0; gi < edges.length; gi++) {
      var ge = edges[gi];

      if (ge.type === "ridge") {
        var gNbs = neighbours(gi);
        var hasHipGable = false;
        for (var gn = 0; gn < gNbs.length; gn++) {
          var gt = edges[gNbs[gn]].type;
          if (gt === "hip" || gt === "gable") {
            hasHipGable = true;
            break;
          }
        }
        if (!hasHipGable && gNbs.length > 0) {
          ge.meta.graphWarning = "ridge_no_hip_gable_neighbour";
        }
      }

      if (ge.type === "valley") {
        var vNbs = neighbours(gi);
        var hasEave = false;
        for (var vn = 0; vn < vNbs.length; vn++) {
          if (edges[vNbs[vn]].type === "eave") {
            hasEave = true;
            break;
          }
        }
        // Valleys that don't reach an eave might be internal; just annotate
        if (!hasEave && vNbs.length > 0) {
          ge.meta.graphNote = "valley_no_eave_neighbour";
        }
      }
    }

    return edges;
  }

  function toRad(d) {
    return (d * Math.PI) / 180;
  }

  function haversineMeters(lon1, lat1, lon2, lat2) {
    var R = 6371000;
    var dLat = toRad(lat2 - lat1);
    var dLon = toRad(lon2 - lon1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function buildRooflineSummary(edges) {
    var summary = {
      totalEdgeLengthMeters: 0,
      ridgeLengthMeters: 0,
      valleyLengthMeters: 0,
      eaveLengthMeters: 0,
      hipLengthMeters: 0,
      gableLengthMeters: 0,
      kneeLengthMeters: 0,
      ridgeCount: 0,
      valleyCount: 0,
      eaveCount: 0,
      hipCount: 0,
      gableCount: 0,
    };

    function getLen(e) {
      if (!e) return 0;
      if (
        typeof e.lengthMeters === "number" &&
        isFinite(e.lengthMeters) &&
        e.lengthMeters > 0
      )
        return e.lengthMeters;
      if (typeof e.length === "number" && isFinite(e.length) && e.length > 0)
        return e.length;
      return 1;
    }

    for (var i = 0; i < edges.length; i++) {
      var e = edges[i];
      var len = getLen(e);
      summary.totalEdgeLengthMeters += len;
      switch (e.type) {
        case "eave":
          summary.eaveCount++;
          summary.eaveLengthMeters += len;
          break;
        case "ridge":
          summary.ridgeCount++;
          summary.ridgeLengthMeters += len;
          break;
        case "valley":
          summary.valleyCount++;
          summary.valleyLengthMeters += len;
          break;
        case "hip":
          summary.hipCount++;
          summary.hipLengthMeters += len;
          break;
        case "gable":
          summary.gableCount++;
          summary.gableLengthMeters += len;
          break;
        default:
          break;
      }
    }

    var r1 = function (v) {
      return Math.round(v * 10) / 10;
    };
    summary.totalEdgeLengthMeters = r1(summary.totalEdgeLengthMeters);
    summary.ridgeLengthMeters = r1(summary.ridgeLengthMeters);
    summary.valleyLengthMeters = r1(summary.valleyLengthMeters);
    summary.eaveLengthMeters = r1(summary.eaveLengthMeters);
    summary.hipLengthMeters = r1(summary.hipLengthMeters);
    summary.gableLengthMeters = r1(summary.gableLengthMeters);
    summary.kneeLengthMeters = r1(summary.kneeLengthMeters);
    return summary;
  }

  function buildFacetRelations(edges) {
    var relations = [];
    for (var i = 0; i < edges.length; i++) {
      var e = edges[i];
      if (e.facetIds.length === 2) {
        relations.push({
          fromFacetId: e.facetIds[0],
          toFacetId: e.facetIds[1],
          edgeId: e.id,
          edgeType: e.type,
        });
      }
    }
    return relations;
  }

  // --- Risk assessment (unchanged) ---
  var RISK_THRESHOLDS = {
    steepDeg: 35,
    verySteepDeg: 45,
    fallHeightMedium: 3,
    fallHeightHigh: 6,
    complexityMedium: 1.05,
    complexityHigh: 1.2,
  };

  var RISK_WEIGHTS = {
    valleyCount: { per: 10, cap: 30 },
    valleyLength: { per: 1.5, cap: 15 },
    verySteep: { per: 10, cap: 20 },
    steep: { per: 5, cap: 10 },
    maxSlope: { per: 0.5, cap: 10 },
    complexity: { per: 25, cap: 15 },
    fallHeight: { per: 2, cap: 10 },
    edgeLength: { per: 0.2, cap: 10 },
  };

  function computeRisk(building) {
    var facets = Array.isArray(building.Facets) ? building.Facets : [];
    var roofline = building.Roofline || { Edges: [] };
    var edgesArr = Array.isArray(roofline.Edges) ? roofline.Edges : [];

    var valleyEdges = edgesArr.filter(function (e) {
      return e.type === "valley";
    });
    var valleyCount = valleyEdges.length;

    var valleyLengthMeters = 0,
      totalEdgeLengthMeters = 0;
    edgesArr.forEach(function (e) {
      if (e.start && e.end && typeof e.start.lon === "number") {
        var len = haversineMeters(
          e.start.lon,
          e.start.lat,
          e.end.lon,
          e.end.lat,
        );
        totalEdgeLengthMeters += len;
        if (e.type === "valley") valleyLengthMeters += len;
      } else {
        totalEdgeLengthMeters += 1;
        if (e.type === "valley") valleyLengthMeters += 1;
      }
    });

    var steepFacetCount = 0,
      verySteepFacetCount = 0,
      maxSlopeDeg = 0;
    facets.forEach(function (f) {
      var s = typeof f.Slope === "number" ? f.Slope : 0;
      if (s >= RISK_THRESHOLDS.steepDeg) steepFacetCount++;
      if (s >= RISK_THRESHOLDS.verySteepDeg) verySteepFacetCount++;
      if (s > maxSlopeDeg) maxSlopeDeg = s;
    });

    var complexityScore =
      building.Roof && typeof building.Roof.ComplexityScore === "number"
        ? building.Roof.ComplexityScore
        : 1;

    var fallHeightMeters =
      building.BuildingInfo &&
      typeof building.BuildingInfo.EavesHeightMaxMeters === "number"
        ? building.BuildingInfo.EavesHeightMaxMeters
        : null;

    var signals = {
      valleyCount: valleyCount,
      valleyLengthMeters: valleyLengthMeters,
      steepFacetCount: steepFacetCount,
      verySteepFacetCount: verySteepFacetCount,
      maxSlopeDeg: maxSlopeDeg,
      complexityScore: complexityScore,
      totalEdgeLengthMeters: totalEdgeLengthMeters,
      fallHeightMeters: fallHeightMeters,
    };

    var score = 0;
    score += Math.min(
      valleyCount * RISK_WEIGHTS.valleyCount.per,
      RISK_WEIGHTS.valleyCount.cap,
    );
    score += Math.min(
      valleyLengthMeters * RISK_WEIGHTS.valleyLength.per,
      RISK_WEIGHTS.valleyLength.cap,
    );
    score += Math.min(
      verySteepFacetCount * RISK_WEIGHTS.verySteep.per,
      RISK_WEIGHTS.verySteep.cap,
    );
    score += Math.min(
      steepFacetCount * RISK_WEIGHTS.steep.per,
      RISK_WEIGHTS.steep.cap,
    );
    if (maxSlopeDeg > 30) {
      score += Math.min(
        (maxSlopeDeg - 30) * RISK_WEIGHTS.maxSlope.per,
        RISK_WEIGHTS.maxSlope.cap,
      );
    }
    if (complexityScore > 1) {
      score += Math.min(
        (complexityScore - 1) * RISK_WEIGHTS.complexity.per,
        RISK_WEIGHTS.complexity.cap,
      );
    }
    if (typeof fallHeightMeters === "number") {
      score += Math.min(
        fallHeightMeters * RISK_WEIGHTS.fallHeight.per,
        RISK_WEIGHTS.fallHeight.cap,
      );
    }
    score += Math.min(
      totalEdgeLengthMeters * RISK_WEIGHTS.edgeLength.per,
      RISK_WEIGHTS.edgeLength.cap,
    );
    score = Math.round(Math.min(100, score));

    var OverallLevel = "Low";
    if (score >= 75) OverallLevel = "Extreme";
    else if (score >= 50) OverallLevel = "High";
    else if (score >= 25) OverallLevel = "Moderate";

    var Reasons = [];
    if (valleyCount > 0) Reasons.push("Multiple valleys increase leak risk");
    if (verySteepFacetCount > 0)
      Reasons.push("Very steep roof sections increase fall risk");
    else if (steepFacetCount > 0)
      Reasons.push("Steep roof increases work difficulty");
    if (
      typeof fallHeightMeters === "number" &&
      fallHeightMeters >= RISK_THRESHOLDS.fallHeightMedium
    ) {
      Reasons.push("Significant fall height increases safety requirements");
    }
    if (complexityScore > RISK_THRESHOLDS.complexityHigh) {
      Reasons.push("High complexity score indicates many roof details");
    }
    if (totalEdgeLengthMeters >= 30) {
      Reasons.push("Long roof edges increase edge work");
    }

    return {
      OverallLevel: OverallLevel,
      Score: score,
      Reasons: Reasons,
      Signals: signals,
    };
  }

  var buildings = [];

  if (Array.isArray(calculatorState.buildings)) {
    calculatorState.buildings.forEach(function (building, buildingIndex) {
      var groundZLookup = buildGroundZLookup(buildingsHeights, building.ByggId);

      // Check roof.selected and set building.selected
      var selected = false;
      for (var ri = 0; ri < building.roofs.length; ri++) {
        if (building.roofs[ri].selected) {
          selected = true;
          break;
        }
      }
      building.selected = selected;

      if (building && building.selected) {
        var existing = Array.isArray(outputJson.Buildings)
          ? outputJson.Buildings[buildingIndex] || {}
          : {};

        var buildingId = "";
        if (
          typeof building.ByggId !== "undefined" &&
          building.ByggId !== null
        ) {
          buildingId = String(building.ByggId);
        } else if (typeof existing.ID !== "undefined" && existing.ID !== null) {
          buildingId = String(existing.ID);
        } else {
          buildingId = "building_" + (buildingIndex + 1);
        }

        var buildingLabel = "";
        if (existing.BuildingLabel) {
          buildingLabel = existing.BuildingLabel;
        } else if (building.name) {
          buildingLabel = building.name;
        } else {
          buildingLabel = "Bygg " + String.fromCharCode(65 + buildingIndex);
        }

        // Build facets from selected roofs
        var roofs = Array.isArray(building.roofs)
          ? building.roofs.filter(function (r) {
              return r && r.selected;
            })
          : [];

        var facets = roofs.map(function (roof, roofIndex) {
          var facetData = roof.facetData || {};
          var facetId = null;
          if (typeof facetData.Id !== "undefined" && facetData.Id !== null) {
            facetId = facetData.Id;
          } else if (typeof roof.id !== "undefined" && roof.id !== null) {
            facetId = roof.id;
          } else {
            facetId = buildingId + "_" + (roofIndex + 1);
          }

          var areaValue = 0;
          if (typeof facetData.Areal3D !== "undefined")
            areaValue = toNumber(facetData.Areal3D);
          else if (typeof roof.area !== "undefined")
            areaValue = toNumber(roof.area);
          else if (typeof facetData.Areal !== "undefined")
            areaValue = toNumber(facetData.Areal);

          var materialValue = "";
          var materialsSelection = calculatorState.selections.materials || {};
          if (materialsSelection[building.ByggId])
            materialValue = materialsSelection[building.ByggId];
          else if (existing.Material) materialValue = existing.Material;
          else if (facetData.Materiale)
            materialValue = String(facetData.Materiale);

          var slopeValue = 0;
          if (typeof facetData.Helning !== "undefined")
            slopeValue = toNumber(facetData.Helning);
          else if (typeof facetData.Vinkel !== "undefined")
            slopeValue = toNumber(facetData.Vinkel);

          var directionValue = 0;
          if (typeof facetData.Retning !== "undefined")
            directionValue = toNumber(facetData.Retning);

          var geometryRaw = facetData.Geometri || facetData.Geometry || null;
          var geometryParsed = parseGeometry(geometryRaw);
          var geometryRel = geometryToRelativeZ(geometryParsed, groundZLookup);

          return {
            id: facetId,
            Selected: true,
            Area: areaValue,
            Material: materialValue,
            Slope: slopeValue,
            Direction: directionValue,
            Geometry: geometryRel,
          };
        });

        // ── Step 0: Compute metric reference point (centroid of all vertices) ──
        var lon0 = 0,
          lat0 = 0,
          nPts = 0;
        facets.forEach(function (f) {
          if (f.Geometry && f.Geometry.type === "Polygon") {
            f.Geometry.coordinates[0].forEach(function (p) {
              lon0 += p[0];
              lat0 += p[1];
              nPts++;
            });
          }
        });
        if (nPts > 0) {
          lon0 /= nPts;
          lat0 /= nPts;
        } else {
          lon0 = 10;
          lat0 = 59;
        } // fallback (Norway)

        // ── Steps 1-2: Build internal facets with metric coords & normals ──
        var internalFacets = [];
        for (var fi = 0; fi < facets.length; fi++) {
          var inf = buildInternalFacet(facets[fi], buildingId, lon0, lat0);
          if (inf && isFacetValid(inf)) internalFacets.push(inf);
        }

        // ── Step 1: Build edge topology ──
        var edgeMap = buildEdgeMap(internalFacets);

        // ── Steps 3-4: Classify edges ──
        var edges = classifyAllEdges(edgeMap, internalFacets);

        // ── Step 5: Post-process ──
        edges = postProcessEdges(edges, internalFacets);

        // ── Summaries ──
        var rooflineSummary = buildRooflineSummary(edges);
        var facetRelations = buildFacetRelations(edges);

        // ── Compute eaves heights per facet ──
        facets.forEach(function (f) {
          var internal = null;
          for (var q = 0; q < internalFacets.length; q++) {
            if (internalFacets[q].facetId === f.id) {
              internal = internalFacets[q];
              break;
            }
          }
          if (!internal) return;

          var eaveEdges = edges.filter(function (e) {
            return e.type === "eave" && e.facetIds.indexOf(f.id) !== -1;
          });

          var heights = [];
          eaveEdges.forEach(function (edge) {
            var gz = groundZForEdge(edge, groundZLookup);
            if (typeof gz !== "number") return;
            heights.push(edge.heightAvg);
          });

          // Fallback: lowest roof point
          if (!heights.length && f.Geometry && f.Geometry.type === "Polygon") {
            f.Geometry.coordinates[0].forEach(function (pt) {
              var gz2 =
                groundZLookup[pt[0].toFixed(9) + "|" + pt[1].toFixed(9)];
              if (typeof pt[2] === "number" && typeof gz2 === "number")
                heights.push(pt[2]);
            });
          }

          f.EavesHeightMeters = heights.length
            ? Math.round(Math.min.apply(null, heights) * 100) / 100
            : null;
        });

        // ── Adjust geometry Z to relative ──
        facets.forEach(function (f) {
          if (!f.Geometry || f.Geometry.type !== "Polygon") return;
          f.Geometry = {
            type: "Polygon",
            coordinates: [
              f.Geometry.coordinates[0].map(function (pt) {
                var gz3 =
                  groundZLookup[pt[0].toFixed(9) + "|" + pt[1].toFixed(9)];
                if (typeof pt[2] === "number" && typeof gz3 === "number") {
                  return [pt[0], pt[1], Math.round((pt[2] - gz3) * 100) / 100];
                }
                return [pt[0], pt[1], pt[2]];
              }),
            ],
          };
        });

        // Roof area
        var areaFromFacets = facets.reduce(function (sum, f) {
          return sum + toNumber(f.Area);
        }, 0);
        var roofArea = areaFromFacets;
        if (roofArea === 0) {
          if (typeof existing.RoofArea !== "undefined")
            roofArea = toNumber(existing.RoofArea);
          else if (typeof building.totalRoofArea !== "undefined")
            roofArea = toNumber(building.totalRoofArea);
        }
        roofArea = Math.round(roofArea * 10) / 10;

        // Roof type (normalize external variants like "Valmettak" → "Valmtak")
        var ROOF_TYPE_ALIASES = { 'Valmettak': 'Valmtak', 'Valmetak': 'Valmtak' };
        var roofType = "";
        if (existing.RoofType) roofType = existing.RoofType;
        else if (outputJson.BuildingType) roofType = outputJson.BuildingType;
        else if (calculatorState.selections.buildingType)
          roofType = calculatorState.selections.buildingType;
        roofType = ROOF_TYPE_ALIASES[roofType] || roofType;

        // Override roof type based on edge analysis when API says "Saltak"
        // but measured geometry has significant hip (valmlinje) edges
        var HIP_MIN_THRESHOLD_M = 2.0;
        if (rooflineSummary && roofType === "Saltak" &&
            rooflineSummary.hipLengthMeters > HIP_MIN_THRESHOLD_M) {
          roofType = "Valmtak";
        }

        // Roof age
        var roofAge = "";
        if (existing.RoofAgeNO) roofAge = existing.RoofAgeNO;
        else if (outputJson.RoofAgeNO) roofAge = outputJson.RoofAgeNO;
        else if (calculatorState.selections.roofAgeNO)
          roofAge = calculatorState.selections.roofAgeNO;
        else if (existing.RoofAge) roofAge = existing.RoofAge;
        else if (outputJson.RoofAge) roofAge = outputJson.RoofAge;
        else if (calculatorState.selections.roofAge)
          roofAge = calculatorState.selections.roofAge;

        // Material
        var buildingMaterial = "";
        var matSel = calculatorState.selections.materials || {};
        if (matSel[building.ByggId]) buildingMaterial = matSel[building.ByggId];
        else if (existing.Material) buildingMaterial = existing.Material;
        else if (facets.length > 0 && facets[0].Material)
          buildingMaterial = facets[0].Material;

        // AI / Complexity
        var aiConfidence =
          typeof existing.AIConfidence === "number" ? existing.AIConfidence : 0;
        var complexityScore =
          typeof existing.ComplexityScore === "number"
            ? existing.ComplexityScore
            : 1;

        // Job type
        var buildingJobType = "";
        if (existing.JobType) buildingJobType = existing.JobType;
        else if (outputJson.JobType) buildingJobType = outputJson.JobType;
        else if (
          wdsCalculatorData &&
          wdsCalculatorData.JobType &&
          wdsCalculatorData.JobType.job
        ) {
          buildingJobType = wdsCalculatorData.JobType.job;
        }

        var roofObject = {
          Area: roofArea,
          Type: roofType,
          Age: roofAge,
          Material: buildingMaterial,
          AIConfidence: aiConfidence,
          ComplexityScore: complexityScore,
          JobType: buildingJobType,
        };

        // Prices
        var pricesObject = {
          Takbytte: toNumber(existing.EstimatedPrice_Takbytte),
          Rehab: toNumber(existing.EstimatedPrice_Rehab),
          Takfornying: toNumber(existing.EstimatedPrice_Takfornying),
          Cleaning: toNumber(existing.EstimatedPrice_Cleaning),
          Enova: {
            Selected: !!existing.Enova_selected,
            Price: existing.enovaSupportEstimated,
            enovaSupportEstimated: existing.enovaSupportEstimated,
            enovaEligible: existing.enovaEligible,
            insulationMode: existing.insulationMode,
            insulationEstimatedPrice: existing.insulationEstimatedPrice,
            insulationVisible: existing.insulationVisible,
          },
        };

        // Building info
        var buildingTypeCode = Number(building.ByggType) || Number("999");
        var floors =
          typeof building.Floors === "number"
            ? building.Floors
            : building.BuildingInfo &&
                typeof building.BuildingInfo.Floors === "number"
              ? building.BuildingInfo.Floors
              : null;

        var eavesHeights = facets
          .map(function (f) {
            return f.EavesHeightMeters;
          })
          .filter(function (v) {
            return typeof v === "number";
          });
        var eavesHeightMin = eavesHeights.length
          ? Math.min.apply(null, eavesHeights)
          : null;
        var eavesHeightMax = eavesHeights.length
          ? Math.max.apply(null, eavesHeights)
          : null;

        var buildingInfo = {
          BuildingTypeCode: buildingTypeCode,
          BuildingTypeGroup: mapBuildingTypeGroup(buildingTypeCode),
          Floors: floors,
          EavesHeightMinMeters: eavesHeightMin,
          EavesHeightMaxMeters: eavesHeightMax,
        };

        var roofline = {
          Edges: edges,
          Summary: rooflineSummary,
          FacetRelations: facetRelations,
        };

        var risk = computeRisk({
          Facets: facets,
          Roofline: roofline,
          Roof: roofObject,
          BuildingInfo: buildingInfo,
        });

        buildings.push({
          ID: buildingId,
          Label: buildingLabel,
          Roof: roofObject,
          Prices: pricesObject,
          BuildingInfo: buildingInfo,
          Facets: facets,
          Roofline: roofline,
          Risk: risk,
        });
      }
    });
  }

  var total = 0;
  calculatorState.buildings.forEach(function (building) {
    total += toNumber(building.EstimatedPrice_Total);
  });

  var projectJobType = "";
  if (
    calculatorState.buildings.length &&
    calculatorState.buildings[0].JobType
  ) {
    projectJobType = calculatorState.buildings[0].JobType;
  } else if (
    wdsCalculatorData &&
    wdsCalculatorData.JobType &&
    wdsCalculatorData.JobType.job
  ) {
    projectJobType = wdsCalculatorData.JobType.job;
  } else {
    projectJobType = outputJson.JobType;
  }

  var projectTimeline = outputJson.Timeline || "";
  var projectMessage = outputJson.FormMessage || "";

  var lat = null,
    lng = null;
  if (outputJson.Tech && typeof outputJson.Tech.CoordinateY === "number")
    lat = outputJson.Tech.CoordinateY;
  if (outputJson.Tech && typeof outputJson.Tech.CoordinateX === "number")
    lng = outputJson.Tech.CoordinateX;

  var roofImageUrl = outputJson.RoofImageURL || "";
  var estimationUrl = outputJson.edit_project_page || "";
  if (!roofImageUrl && calculatorState.mapUrl)
    roofImageUrl = calculatorState.mapUrl;
  if (
    !estimationUrl &&
    typeof window !== "undefined" &&
    window.location &&
    window.location.href
  ) {
    estimationUrl = window.location.href;
  }

  var calculatorVersion =
    outputJson.Tech && outputJson.Tech.CalculatorVersion
      ? outputJson.Tech.CalculatorVersion
      : "v3.0";
  var pricingModelVersion =
    outputJson.Tech && outputJson.Tech.PricingModelVersion
      ? outputJson.Tech.PricingModelVersion
      : "v3.2";
  var landingPageUrl =
    outputJson.Tech && outputJson.Tech.LandingPageURL
      ? outputJson.Tech.LandingPageURL
      : typeof window !== "undefined" && window.location
        ? window.location.href
        : "";

  var aiRecommendation = outputJson.AIRecommendation || "";
  var aiTechnicalSummary = outputJson.TechnicalSummary || "";

  var dateReceived = outputJson.DateReceived || new Date().toISOString();
  var leadId = outputJson.LeadID;
  if (leadId === null || typeof leadId === "undefined") leadId = null;

  return {
    LeadID: leadId,
    SubmittedVia: outputJson.SubmittedVia || "tak-kalkulator",
    DateReceived: dateReceived,
    Customer: {
      FullName: outputJson.FullName || "",
      Phone: outputJson.Phone || "",
      Email: outputJson.Email || "",
    },
    Address: {
      StreetAddress: streetAddress || "",
      PostalCode: outputJson.PostalCode || "",
      City: outputJson.City || "",
      FullAddress: fullAddress,
    },
    Project: {
      JobType: projectJobType,
      Timeline: projectTimeline,
      Message: projectMessage,
    },
    Buildings: buildings,
    Total: total,
    AI: {
      Recommendation: aiRecommendation,
      TechnicalSummary: aiTechnicalSummary,
    },
    Assets: {
      RoofImageURL: roofImageUrl,
      EstimationURL: estimationUrl,
    },
    Tech: {
      Coordinates: { Lat: lat, Lng: lng },
      CalculatorVersion: calculatorVersion,
      PricingModelVersion: pricingModelVersion,
      LandingPageURL: landingPageUrl,
    },
  };
}

async function updateBuildingsHeights(wdsCalculatorData, calculatorState) {
  var formData = new FormData();
  formData.append("action", "wds_update_buildings_heights");
  formData.append("nonce", wdsCalculatorData.nonce);
  formData.append("data", JSON.stringify(calculatorState.buildings));
  try {
    var response = await fetch(wdsCalculatorData.ajax_url, {
      method: "POST",
      body: formData,
    });
    var json = await response.json();
    return json.data.details;
  } catch (error) {
    console.error("Error:", error);
    throw error;
  }
}
